import { useCallback, useEffect, useState } from 'react';
import { useT } from '../i18n';
import { useAuth } from '../context/AuthContext';
import { useRole } from '../context/RoleContext';
import { api } from '../api/client';
import { useLiveChanges } from '../live/live';
import type { GridColumn, HomeData } from '../types';
import { MyTasks } from '../components/home/MyTasks';
import { SponsoredProjects } from '../components/home/SponsoredProjects';
import { HomeTaskPanel } from '../components/home/TaskPanel';
import { invalidateHomeProjects } from '../components/home/useHomeProject';

// Accueil — where everyone lands after logging in. Two sections, never mixed:
// my own tasks, grouped by project, and the projects I sponsor, each drawn
// whole (goal, planning, milestones and tasks, team, meetings, news).
export function HomePage() {
  const { t, lang } = useT();
  const { user } = useAuth();
  const { isAdmin } = useRole();
  const [data, setData] = useState<HomeData | null>(null);
  const [columns, setColumns] = useState<GridColumn[]>([]);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(() => {
    api.getHome().then(d => { setData(d); setError(null); }).catch((e: Error) => setError(e.message));
  }, []);
  useEffect(() => {
    load();
    (api.getKanbanColumns() as Promise<GridColumn[]>).then(setColumns).catch(() => {});
  }, [load]);
  useLiveChanges(() => load());

  const firstName = (user?.name || '').trim().split(/\s+/)[0] || '';
  const today = new Date().toLocaleDateString(lang === 'en' ? 'en-GB' : 'fr-CH', { weekday: 'long', day: 'numeric', month: 'long' });
  const sponsored = data?.projects.filter(p => p.is_sponsor) ?? [];
  const empty = !!data && data.tasks.length === 0 && sponsored.length === 0;

  return (
    <>
      <div className="page-header">
        <div>
          <h1 className="page-title">{firstName ? t('home.hello', { name: firstName }) : t('home.helloAnon')}</h1>
          <p className="page-meta">{today.charAt(0).toUpperCase() + today.slice(1)}</p>
        </div>
      </div>

      <div className="page home-page">
      {error && <div className="home-panel home-panel--message">{error}</div>}
      {!data && !error && <div className="spinner" />}

      {data && (
        <HomeTaskPanel onUpdated={() => { load(); invalidateHomeProjects(); }}>
        <div className="home-sections">
          <MyTasks data={data} columns={columns} onChanged={load} />
          <SponsoredProjects projects={sponsored} onTasksChanged={load} />
          {empty && (
            <div className="empty-state">
              <div className="empty-state-title">{t('home.empty.title')}</div>
              <div className="empty-state-desc">{t('home.empty.desc')}</div>
              <div className="home-empty-actions">
                <a className="btn btn-sm" href="#portfolio">{t('home.empty.portfolio')}</a>
                {!isAdmin && <a className="btn btn-accent btn-sm" href="#submit">{t('home.empty.submit')}</a>}
              </div>
            </div>
          )}
        </div>
        </HomeTaskPanel>
      )}
      </div>
    </>
  );
}
